// Module Import
//---------------------------------
import React from 'react'
import { Link } from 'gatsby-link'

import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'

import {
  brandBlack,
  brandDarkGrey,
  brandIvory,
  brandKhaki,
  brandOrange,
  brandSoftOrange,
  brandWhite,
} from '../../../assets/consts/color'
import { primaryFont, secondaryFont } from '../../../assets/consts/fonts'

// Styled Component Declarations
//---------------------------------

const LinkText = styled.a`
  a {
    color: ${brandDarkGrey};
    font-family: '${secondaryFont}';
    font-size: 23px;
    font-style: normal;
    font-weight: 200;
    line-height: 120%;
    opacity: 75%;
    text-decoration: none;

    &:hover {
      color: ${brandOrange};
      opacity: 1;
    }

    @media only screen and (min-width: 48em) {
      font-size: 39px;
    }
  }
`

const LinkAuthor = styled.a`
  a {
    color: ${brandOrange};
    font-family: '${primaryFont}';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    @media only screen and (min-width: 48em) {
      font-size: 16px;
    }
  }
`

const footerLink = css`
  color: ${brandDarkGrey};
  font-family: ${primaryFont};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  text-decoration: none;

  &:hover {
    color: ${brandOrange};
  }

  &:visited {
    color: ${brandDarkGrey};

    &:hover {
      color: ${brandOrange};
    }
  }
`

const FooterLink = styled.a`
  ${footerLink}
`

const FooterInnerLink = styled(Link)`
  ${footerLink}
`

const LinkTextBody = styled.span`
  a {
    color: ${brandDarkGrey};
    font-family: ${primaryFont};
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%;
    opacity: 75%;
    text-decoration: none;

    &:hover {
      color: ${brandOrange};
      opacity: 1;
    }

    &:visited {
      color: ${brandDarkGrey};

      &:hover {
        color: ${brandOrange};
      }
    }
  }
`

const LinkCTA = styled.a`
  align-items: center;
  background: ${brandOrange};
  border: 0;
  box-shadow: 0px 0px 0px 0px ${brandBlack};
  color: ${brandWhite};
  display: flex;
  flex-direction: column;
  font-family: ${secondaryFont};
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  justify-content: center;
  line-height: 120%; /* 24px */
  padding: 16px 32px 16px 16px;
  position: relative;
  text-decoration: none;
  transition:
    background-color 0.25s ease-in-out,
    color 0.25s ease-in-out;

  &:after {
    background-color: ${brandWhite};
    bottom: -16px;
    content: '';
    height: 32px;
    position: absolute;
    right: -16px;
    transform: rotate(-45deg);
    width: 32px;
    z-index: 1;
  }

  a {
    color: ${brandWhite};
    text-decoration: none;
    transition:
      background-color 0.25s ease-in-out,
      color 0.25s ease-in-out;
  }

  &:hover {
    background: ${brandSoftOrange};

    a {
      color: ${brandOrange};
    }
  }
`

// ---------- Cut Button ----------//
const LinkCTALarge = styled.a`
  align-items: center;
  background: ${brandOrange};
  border: 0;
  box-shadow: 0px 0px 0px 0px ${brandBlack};
  color: ${brandWhite};
  display: flex;
  flex-direction: column;
  font-family: '${secondaryFont}';
  text-decoration: none;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  justify-content: center;
  line-height: 120%; /* 24px */
  padding: 40px;
  position: relative;
  transition:
    background-color 0.25s ease-in-out,
    color 0.25s ease-in-out;

  &:visited {
    color: ${brandWhite};
  }

  &:after {
    background-color: ${brandWhite};
    bottom: -7px;
    content: '';
    height: 24px;
    position: absolute;
    right: -15px;
    transform: rotate(-45deg);
    width: 40px;
    z-index: 1;
  }

  &:hover {
    background: ${brandSoftOrange};
    color: ${brandOrange};
  }
`

// ---------- Simple Button ----------//
const SimpleButton = css`
  position: relative;
  display: inline-flex;
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  padding: 16px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border: 2px solid ${brandOrange};
  box-shadow: 0px 0px 0px 0px ${brandBlack};
  color: ${brandOrange};
  text-decoration: none;

  /* typography/H5/Bold */
  font-family: '${secondaryFont}';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%; /* 24px */
  transition: 0.25s ease-in-out;

  &:hover {
    color: ${brandWhite};
    background-color: ${brandOrange};

    &:visited {
      color: ${brandWhite};
    }
  }

  &:visited {
    color: ${brandOrange};
  }

  @media only screen and (min-width: 48em) {
    font-size: 20px;
  }
`

const SimpleButtonOuterLink = styled.a`
  ${SimpleButton}
`

const SimpleButtonOrangeSecond = styled(Link)`
  ${SimpleButton}
  padding: 32px 40px;
  font-size: 20px;
  background-color: ${brandOrange};
  color: ${brandWhite};

  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    right: -3px;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 42px 42px 0 0;
    border-color: transparent ${brandWhite} transparent transparent;
    opacity: 0;
    transition: 0.25s ease-in-out;
  }

  &:visited {
    background-color: ${brandOrange};
    color: ${brandWhite};
  }

  &:hover {
    color: ${brandOrange};
    background-color: ${brandWhite};
    border-color: ${brandOrange};

    &:visited {
      color: ${brandOrange};
      background-color: ${brandWhite};
      border-color: ${brandOrange};
    }

    &::after {
      opacity: 1;
    }
  }

  &:active {
    color: ${brandWhite};
    background-color: ${brandOrange};
    border-color: transparent;

    &:visited {
      color: ${brandWhite};
    }

    &::after {
      opacity: 1;
    }
  }
`

const SimpleButtonOrange = styled(Link)`
  ${SimpleButton}
  padding: 32px 40px;
  font-size: 20px;

  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    right: -3px;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 42px 42px 0 0;
    border-color: transparent ${brandWhite} transparent transparent;
    opacity: 0;
    transition: 0.25s ease-in-out;
  }

  &:hover {
    color: ${brandWhite};
    background-color: ${brandOrange};
    border-color: transparent;

    &:visited {
      color: ${brandWhite};
    }

    &::after {
      opacity: 1;
    }
  }

  &:active {
    color: ${brandWhite};
    background-color: ${brandOrange};
    border-color: transparent;

    &:visited {
      color: ${brandWhite};
    }

    &::after {
      opacity: 1;
    }
  }
`

const KhakiButtonLink = styled(SimpleButtonOrange)`
  color: ${brandWhite};
  background-color: ${brandKhaki};
  border-color: ${brandKhaki};

  &:visited {
    color: ${brandWhite};
  }

  &:hover {
    color: ${brandWhite};
    background-color: ${brandKhaki};
    filter: opacity(80%);

    &:visited {
      color: ${brandWhite};
    }
  }
`

const SimpleButtonWhite = styled(Link)`
  display: inline-flex;
  padding: 16px 24px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border: 2px solid ${brandOrange};
  box-shadow: 0px 0px 0px 0px ${brandBlack};
  color: ${brandOrange};
  text-decoration: none;

  &:visited {
    color: ${brandOrange};
  }

  /* typography/H5/Bold */
  font-family: '${secondaryFont}';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%; /* 24px */
  transition: 0.25s ease-in-out;

  &:hover {
    color: ${brandWhite};
    background-color: ${brandOrange};
  }

  @media only screen and (min-width: 48em) {
    font-size: 20px;
  }
`

const GoRentalButton = styled.div`
  margin-top: 16px;

  a {
    cursor: pointer;
    color: var(--Brand---Orange, ${brandOrange});
    font-family: ${primaryFont};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%; /* 24px */
  }
`

const GoUpgradeButton = styled(GoRentalButton)`
  margin-top: 0;
`

export const FeatureCardButton = styled(Link)`
  align-items: flex-start;
  align-self: stretch;
  background-color: ${brandIvory};
  color: ${brandDarkGrey};
  display: flex;
  flex-direction: column;
  font-family: ${secondaryFont};
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  padding: 32px 48px;
  position: relative;
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color 0.25s ease-in-out,
    color 0.25s ease-in-out;

  &:after {
    background-color: ${brandWhite};
    bottom: -7px;
    content: '';
    height: 24px;
    position: absolute;
    right: -15px;
    transform: rotate(-45deg);
    width: 40px;
    z-index: 1;
  }

  &:visited {
    color: ${brandDarkGrey};
  }

  &:hover {
    background-color: ${brandOrange};
    color: ${brandWhite};
  }
`
const AsideArticleButton = styled(FeatureCardButton)`
  background-color: ${brandOrange};
  color: ${brandWhite};
  font-family: ${secondaryFont};

  &:hover {
    background-color: ${brandSoftOrange};
    color: ${brandOrange};
  }

  &:visited {
    color: ${brandWhite};
    &:hover {
      color: ${brandOrange};
    }
  }
`

// Component Export
//---------------------------------
export default function LinkAtom({ linkDestination, linkText, linkType, ...prop }) {
  return (
    <>
      {linkType === 'Text' && (
        <LinkText>
          <Link {...prop} to={linkDestination}>
            {linkText}
          </Link>
        </LinkText>
      )}

      {linkType === 'Text Body' && (
        <LinkTextBody>
          <Link {...prop} to={linkDestination}>
            {linkText}
          </Link>
        </LinkTextBody>
      )}

      {linkType === 'Primary CTA' && (
        <LinkCTA>
          <Link {...prop} to={linkDestination}>
            {linkText}
          </Link>
        </LinkCTA>
      )}
      {linkType === 'Primary Large CTA' && (
        <LinkCTALarge {...prop} href={linkDestination}>
          {linkText}
        </LinkCTALarge>
      )}
      {linkType === 'Simple Button Link' && (
        <SimpleButtonOrange {...prop} to={linkDestination}>
          {linkText}
        </SimpleButtonOrange>
      )}
      {linkType === 'Simple Orange Button Link' && (
        <SimpleButtonOrangeSecond {...prop} to={linkDestination}>
          {linkText}
        </SimpleButtonOrangeSecond>
      )}
      {linkType === 'Simple Button Outer Link' && (
        <SimpleButtonOuterLink {...prop} href={linkDestination}>
          {linkText}
        </SimpleButtonOuterLink>
      )}
      {linkType === 'Khaki Button Link' && (
        <KhakiButtonLink {...prop} to={linkDestination}>
          {linkText}
        </KhakiButtonLink>
      )}
      {linkType === 'White Button Link' && (
        <SimpleButtonWhite {...prop} to={linkDestination}>
          {linkText}
        </SimpleButtonWhite>
      )}
      {linkType === 'Footer Link' && (
        <FooterLink {...prop} href={linkDestination}>
          {linkText}
        </FooterLink>
      )}
      {linkType === 'Footer Inner Link' && (
        <FooterInnerLink {...prop} to={linkDestination}>
          {linkText}
        </FooterInnerLink>
      )}
      {linkType === 'Rental Button' && (
        <GoRentalButton>
          <Link to={linkDestination}>{linkText}</Link>
        </GoRentalButton>
      )}
      {linkType === 'Upgrade Button' && (
        <GoUpgradeButton>
          <Link to={linkDestination}>{linkText}</Link>
        </GoUpgradeButton>
      )}
      {linkType === 'Card Button Link' && (
        <FeatureCardButton to={linkDestination}>{linkText}</FeatureCardButton>
      )}
      {linkType === 'Aside Article Link' && (
        <AsideArticleButton as="a" href={linkDestination} {...prop}>
          {linkText}
        </AsideArticleButton>
      )}
      {linkType === 'Author' && (
        <LinkAuthor>
          <Link {...prop} to={linkDestination}>
            {linkText}
          </Link>
        </LinkAuthor>
      )}
    </>
  )
}

LinkAtom.propTypes = {
  linkDestination: PropTypes.string.isRequired,
  linkText: PropTypes.string.isRequired,
  linkType: PropTypes.string.isRequired,
  target: PropTypes.string,
}
